/**
 * 买家端在线咨询数据层：与官网（bluplanet-marketing）同一套客服会话系统
 * （POST /api/ai/customer/public/sessions，凭据 = 会话 id + X-Chat-Token，localStorage 持久化）。
 * 从官网 customerChat.ts 移植：语言跟随买家端（zh/en/ko），文案统一放 i18n 词典。
 */
import { translate, type Lang } from '@/i18n';

export type { Lang } from '@/i18n';
export type Audience = 'GENERAL' | 'BUYER' | 'SUPPLIER';
export interface LeadDetails { audience: Audience; company: string; products: string; requirements: string; submittedAt: string | null }
export interface LeadInput { name: string; contact: string; company: string; products: string; requirements: string }

export interface ChatSession {
  id: string; status: 'AI' | 'WAITING' | 'HUMAN' | 'CLOSED'; thinking: boolean;
  customerName: string; contact: string; version: number;
  lead?: LeadDetails;
}
export interface ChatMessage { seq: number; role: 'CUSTOMER' | 'AI' | 'STAFF' | 'SYSTEM'; content: string; createdAt: string; recalled?: boolean }
export interface ChatView { session: ChatSession; messages: ChatMessage[]; hasMore: boolean; recalledSeqs?: number[] }
export function mergeVisibleMessages(previous: ChatMessage[], view: Pick<ChatView, 'messages' | 'recalledSeqs'>): ChatMessage[] {
  const recalled = new Set(view.recalledSeqs || []);
  const next = [...new Map([...previous, ...view.messages].map(m => [m.seq, m])).values()]
    .filter(m => !m.recalled && !recalled.has(m.seq)).sort((a, b) => a.seq - b.seq);
  return next.length === previous.length && next.every((m, i) => m === previous[i]) ? previous : next;
}
export interface ChatCredential { id: string; token: string }
const key = 'bluplanet.customer-chat.v1';
const storageKey = (audience: Audience) => audience === 'GENERAL' ? key : `${key}.${audience}`;
/** 买家门户登录态凭据键（登出时清除，防同浏览器换账号串会话） */
export const BUYER_CHAT_STORAGE_KEY = `${key}.BUYER`;
export function savedChat(audience: Audience = 'GENERAL'): ChatCredential | null {
  try { const value = JSON.parse(localStorage.getItem(storageKey(audience)) || 'null'); return value?.id && value?.token ? value : null; }
  catch { return null; }
}
export function saveChat(value: ChatCredential | null, audience: Audience = 'GENERAL') {
  try { if (value) localStorage.setItem(storageKey(audience), JSON.stringify(value)); else localStorage.removeItem(storageKey(audience)); } catch { /* memory session still works */ }
}
export function chatRequestId() { return [...crypto.getRandomValues(new Uint8Array(16))].map(b => b.toString(16).padStart(2, '0')).join(''); }
export async function chatRequest<T>(credential: ChatCredential | null, path: string, body?: unknown): Promise<T> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), 15000);
  try {
    const response = await fetch(`/api/ai/customer/public/sessions${credential ? `/${encodeURIComponent(credential.id)}` : ''}${path}`, {
      method: body === undefined ? 'GET' : 'POST', credentials: 'omit', signal: controller.signal,
      headers: { 'Content-Type': 'application/json', ...(credential ? { 'X-Chat-Token': credential.token } : {}) },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    const data = await response.json();
    if (!response.ok || data.code !== 200) throw Object.assign(new Error('Chat request failed'), {
      status: response.status, sessionExpired: data.code === 401 && data.message === 'Session expired',
    });
    return data.data as T;
  } finally { window.clearTimeout(timer); }
}

/** 买家门户登录态通道（/api/ai/customer/sessions：网关校验买家 JWT 后注入 X-User-Id）。 */
export async function buyerChatRequest<T>(bearer: string, path: string, body?: unknown): Promise<T> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), 15000);
  try {
    const response = await fetch(`/api/ai/customer/sessions${path}`, {
      method: body === undefined ? 'GET' : 'POST', credentials: 'omit', signal: controller.signal,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${bearer}` },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    const data = await response.json();
    if (!response.ok || data.code !== 200) throw Object.assign(new Error('Chat request failed'), {
      status: response.status, sessionExpired: false,
    });
    return data.data as T;
  } finally { window.clearTimeout(timer); }
}

/** 抽屉内文案（统一走 i18n 词典 chat.*）；status 键直接索引会话状态副标题 */
const CHAT_TEXT_KEYS = ['AI', 'WAITING', 'HUMAN', 'CLOSED', 'thinking', 'human', 'staff', 'error', 'expired', 'restart', 'send', 'welcome', 'placeholder'] as const;
export type ChatTextKey = typeof CHAT_TEXT_KEYS[number];
export function chatText(lang: Lang): Record<ChatTextKey, string> {
  return Object.fromEntries(CHAT_TEXT_KEYS.map((key) => [key, translate(lang, `chat.${key}`)])) as Record<ChatTextKey, string>;
}
